import { useEffect, useRef, useState } from "react";
import api, { formatApiError, showApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { CalendarDays, Sparkles, RefreshCw, Utensils, ShoppingCart, Printer, CheckCircle2, XCircle, Wand2, Pill, ChefHat, Clock, Bookmark, Volume2, Square, Trash2, Flame, Search, ArrowDownUp, Share2 } from "lucide-react";
import AiLoader from "@/components/AiLoader";
import { PremiumLock, openUpgrade } from "@/components/PremiumLock";
import { Lock, Gift } from "lucide-react";
import CookingMode from "@/components/nutri/CookingMode";
import { buildRecipeCardCanvas } from "@/lib/recipeCard";
import { toast } from "sonner";

const MEAL_COLORS = {
  Colazione: "bg-amber-100 text-amber-900 border-amber-300",
  Spuntino: "bg-emerald-100 text-emerald-900 border-emerald-300",
  Pranzo: "bg-orange-100 text-orange-900 border-orange-300",
  Merenda: "bg-yellow-100 text-yellow-900 border-yellow-300",
  Cena: "bg-lime-100 text-lime-900 border-lime-300",
};

const CompareBar = ({ label, unit, proposed, target, testid }) => {
  if (proposed == null || target == null) return null;
  const max = Math.max(proposed, target, 1);
  const over = proposed > target;
  const pct = target ? Math.round(((proposed - target) / target) * 100) : 0;
  return (
    <div className="space-y-1" data-testid={testid}>
      <div className="flex justify-between text-xs">
        <span className="font-medium">{label}</span>
        <span className={over ? "text-destructive font-medium" : "text-emerald-600 font-medium"}>
          {proposed}{unit} vs {target}{unit}{target ? ` (${pct >= 0 ? "+" : ""}${pct}%)` : ""}
        </span>
      </div>
      <div className="relative h-2 rounded-full bg-muted overflow-hidden">
        <div className="absolute inset-y-0 left-0 bg-foreground/25 rounded-full" style={{ width: `${(target / max) * 100}%` }} />
        <div className={`absolute inset-y-0 left-0 rounded-full ${over ? "bg-destructive/70" : "bg-emerald-500/70"}`} style={{ width: `${(proposed / max) * 100}%`, mixBlendMode: "multiply" }} />
      </div>
    </div>
  );
};

const MealCard = ({ meal, testid }) => (
  <div className="rounded-xl border p-3 bg-background" data-testid={testid}>
    <div className="font-medium mb-1">{meal.name}</div>
    <ul className="text-sm text-muted-foreground space-y-0.5 mb-2">
      {(meal.items || []).map((it, i) => (
        <li key={i} className="flex justify-between"><span>{it.food}</span><span className="font-mono-data">{it.grams}g · {it.kcal} kcal</span></li>
      ))}
    </ul>
    <div className="flex flex-wrap gap-2 text-xs">
      <span className="rounded-md bg-amber-100 text-amber-900 px-2 py-0.5 font-mono-data">{meal.kcal} kcal</span>
      <span className="rounded-md bg-emerald-100 text-emerald-900 px-2 py-0.5 font-mono-data">P {meal.protein_g}g</span>
      <span className="rounded-md bg-orange-100 text-orange-900 px-2 py-0.5 font-mono-data">C {meal.carbs_g}g</span>
      <span className="rounded-md bg-yellow-100 text-yellow-900 px-2 py-0.5 font-mono-data">G {meal.fat_g}g</span>
    </div>
    {meal.note && <p className="text-xs text-muted-foreground mt-2 italic">{meal.note}</p>}
  </div>
);

export default function WeeklyDiet({ initialAction } = {}) {
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [target, setTarget] = useState(null); // {day, meal_type}
  const [proposal, setProposal] = useState("");
  const [proposing, setProposing] = useState(false);
  const [result, setResult] = useState(null); // {approved, reason, suggestion}
  const [shopOpen, setShopOpen] = useState(false);
  const [shopItems, setShopItems] = useState([]);
  const [shopOrder, setShopOrder] = useState([]);
  const [shopLoading, setShopLoading] = useState(false);
  const [shopChecked, setShopChecked] = useState({});
  const [supps, setSupps] = useState([]);
  const [recipe, setRecipe] = useState(null); // {loading, data, mealName, meal, servings}
  const [speaking, setSpeaking] = useState(false);
  const [cooking, setCooking] = useState(null);
  const [savedIds, setSavedIds] = useState([]); // names saved this session
  const [savedOpen, setSavedOpen] = useState(false);
  const [savedList, setSavedList] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [savedQuery, setSavedQuery] = useState("");
  const [savedSort, setSavedSort] = useState("recent");
  const [shopMode, setShopMode] = useState("base");
  const [recipeShop, setRecipeShop] = useState(null); // categories[]
  const [recipeShopLoading, setRecipeShopLoading] = useState(false);
  const [dietStyle, setDietStyle] = useState("onnivoro");
  const [favMax, setFavMax] = useState(3);
  const [regenRequired, setRegenRequired] = useState(false);
  const [savingStyle, setSavingStyle] = useState(false);
  const { refreshUser, user } = useAuth();
  const premium = !!user?.is_premium;
  const [lockedDays, setLockedDays] = useState([]);
  const [partial, setPartial] = useState(false);
  const [freeRecipe, setFreeRecipe] = useState(null);
  const guard = (fn) => (...args) => (premium ? fn(...args) : openUpgrade());

  const DIETARY_LABELS = {
    onnivoro: "Onnivoro", vegetariano: "Vegetariano", vegano: "Vegano", pescetariano: "Pescetariano",
  };

  const changeDietStyle = async (value) => {
    if (value === dietStyle) return;
    setSavingStyle(true);
    try {
      const { data } = await api.put("/profile/dietary-style", { dietary_style: value });
      setDietStyle(data.dietary_style);
      setRegenRequired(!!data.diet_regen_required);
      try { await refreshUser(); } catch {}
      if (data.diet_regen_required) {
        toast.warning("Stile alimentare aggiornato: ora devi rigenerare la dieta");
      } else {
        toast.success("Stile alimentare aggiornato");
      }
    } catch (err) { showApiError(err); }
    finally { setSavingStyle(false); }
  };

  const changeFavMax = async (value) => {
    const n = parseInt(value, 10);
    if (n === favMax) return;
    setSavingStyle(true);
    try {
      const { data } = await api.put("/diet/favorite-frequency", { max_per_week: n });
      setFavMax(data.favorite_max_per_week);
      setRegenRequired(!!data.diet_regen_required);
      if (data.diet_regen_required) toast.warning("Frequenza preferiti aggiornata: rigenera la dieta");
      else toast.success("Frequenza preferiti aggiornata");
    } catch (err) { showApiError(err); }
    finally { setSavingStyle(false); }
  };

  const loadRecipeShop = async () => {
    setShopMode("recipes");
    if (recipeShop) return;
    setRecipeShopLoading(true);
    try { const { data } = await api.post("/diet/shopping-list-recipes"); setRecipeShop(data.categories || []); }
    catch (err) { showApiError(err); }
    finally { setRecipeShopLoading(false); }
  };

  const fetchRecipe = async (meal, servings) => {
    setRecipe((r) => ({ loading: true, data: (r && r.mealName === meal.name) ? r.data : null, mealName: meal.name, meal, servings }));
    try {
      const { data } = await api.post("/diet/recipe", { meal_name: meal.name, items: meal.items, meal_type: meal.type, servings });
      setRecipe({ loading: false, data, mealName: meal.name, meal, servings });
      if (!premium && !freeRecipe) setFreeRecipe({ meal_name: meal.name, meal_type: meal.type });
    } catch (err) { showApiError(err); setRecipe(null); }
  };
  const openRecipe = (meal) => fetchRecipe(meal, 1);
  const isGift = (m) => freeRecipe && freeRecipe.meal_name.toLowerCase() === (m.name || "").toLowerCase();
  const recipeClick = (m) => {
    if (premium || isGift(m)) return openRecipe(m);
    if (freeRecipe) return openUpgrade();
    if (window.confirm(`Hai a disposizione una sola ricetta omaggio. Vuoi usarla per "${m.name}"?`)) openRecipe(m);
  };
  const changeServings = (n) => {
    if (!recipe?.meal || n < 1 || n > 12 || recipe.loading) return;
    fetchRecipe(recipe.meal, n);
  };

  const recipeTitle = () => recipe?.data?.title || recipe?.mealName || "Ricetta";

  const saveRecipe = async () => {
    if (!recipe?.data) return;
    try {
      await api.post("/recipes/save", { name: recipeTitle(), data: recipe.data });
      setSavedIds((s) => [...s, recipeTitle()]);
      toast.success("Ricetta salvata nei preferiti");
    } catch (err) { showApiError(err); }
  };

  const speakRecipe = () => {
    if (!("speechSynthesis" in window) || !recipe?.data) { toast.error("Lettura vocale non disponibile"); return; }
    if (speaking) { window.speechSynthesis.cancel(); setSpeaking(false); return; }
    const d = recipe.data;
    const text = `${recipeTitle()}. Ingredienti: ${(d.ingredients || []).join(", ")}. Preparazione: ${(d.steps || []).map((s, i) => `Passo ${i + 1}. ${s}`).join(" ")}`;
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "it-IT"; u.rate = 0.95;
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
    setSpeaking(true);
  };

  const shareRecipe = async () => {
    if (!recipe?.data) return;
    try {
      const canvas = buildRecipeCardCanvas({ ...recipe.data, title: recipeTitle() });
      canvas.toBlob(async (blob) => {
        if (!blob) { toast.error("Impossibile creare l'immagine"); return; }
        const file = new File([blob], `${recipeTitle()}.png`, { type: "image/png" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          try { await navigator.share({ files: [file], title: recipeTitle(), text: "Guarda questa ricetta da DietFlix!" }); }
          catch { /* user cancelled */ }
        } else {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a"); a.href = url; a.download = `${recipeTitle()}.png`;
          document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
          toast.success("Immagine della ricetta scaricata: pronta da condividere");
        }
      }, "image/png");
    } catch (e) { toast.error("Condivisione non riuscita"); }
  };

  const printRecipe = () => {    if (!recipe?.data) return;
    const d = recipe.data;
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${recipeTitle()}</title>
      <style>body{font-family:-apple-system,Segoe UI,Roboto,Arial,sans-serif;color:#1a1a2e;padding:32px;max-width:640px;margin:auto}
      h1{color:#2E5BFF} .meta{color:#666;margin-bottom:14px} h3{color:#EC0B8C;margin:16px 0 6px} li{margin:3px 0}</style></head><body>
      <h1>${recipeTitle()}</h1>
      <div class="meta">${d.prep_min != null ? `Preparazione ${d.prep_min} min · ` : ""}${d.cook_min != null ? `Cottura ${d.cook_min} min · ` : ""}${d.servings || 1} porzione — DietFlix</div>
      ${d.nutrition ? `<div class="meta">Per porzione: ${d.nutrition.kcal ?? "—"} kcal · Proteine ${d.nutrition.protein_g ?? "—"}g · Carboidrati ${d.nutrition.carbs_g ?? "—"}g · Grassi ${d.nutrition.fat_g ?? "—"}g</div>` : ""}
      ${(d.ingredients || []).length ? `<h3>Ingredienti</h3><ul>${d.ingredients.map((x) => `<li>${x}</li>`).join("")}</ul>` : ""}
      ${(d.steps || []).length ? `<h3>Preparazione</h3><ol>${d.steps.map((x) => `<li>${x}</li>`).join("")}</ol>` : ""}
      ${(d.tips || []).length ? `<h3>Consigli</h3><ul>${d.tips.map((x) => `<li>${x}</li>`).join("")}</ul>` : ""}
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Consenti i popup per stampare"); return; }
    w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 400);
  };

  const openSaved = async () => {
    setSavedOpen(true);
    setSavedLoading(true);
    try { const { data } = await api.get("/recipes/saved"); setSavedList(data.recipes || []); } catch (err) { showApiError(err); }
    finally { setSavedLoading(false); }
  };
  const deleteSaved = async (id) => {
    try { await api.delete(`/recipes/saved/${id}`); setSavedList((l) => l.filter((r) => r.recipe_id !== id)); }
    catch (err) { showApiError(err); }
  };

  const toggleChecked = (food) => setShopChecked((prev) => {
    const n = { ...prev, [food]: !prev[food] };
    try { localStorage.setItem("nc_shop_checked", JSON.stringify(n)); } catch {}
    return n;
  });
  const clearChecked = () => { setShopChecked({}); try { localStorage.removeItem("nc_shop_checked"); } catch {} };

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/diet");
    setPlan(data?.plan || null);
    setLockedDays(data?.locked_days || []);
    setPartial(!!data?.partial);
    setFreeRecipe(data?.free_recipe || null);
    setDietStyle(data?.dietary_style || "onnivoro");
    setFavMax(data?.favorite_max_per_week || 3);
    setRegenRequired(!!data?.diet_regen_required);
    try { const s = await api.get("/supplements"); setSupps(s.data?.supplements || []); } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const actionDone = useRef(false);
  useEffect(() => {
    if (loading || actionDone.current || !initialAction) return;
    actionDone.current = true;
    if (initialAction === "shopping") openShopping();
    else if (initialAction === "saved") openSaved();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, initialAction]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/diet/generate");
      setPlan(data.plan);
      setLockedDays(data.locked_days || []);
      setPartial(!!data.partial);
      setRegenRequired(false);
      toast.success(data.limited ? "Il tuo lunedì su misura è pronto!" : "Dieta settimanale generata!");
    } catch (err) {
      showApiError(err);
    } finally { setGenerating(false); }
  };

  const openPropose = (day, meal_type) => { setTarget({ day, meal_type }); setProposal(""); setResult(null); };

  const doPropose = async (text) => {
    const t = (text ?? proposal).trim();
    if (!t) { toast.error("Descrivi la modifica che vuoi proporre"); return; }
    setProposing(true);
    try {
      const { data } = await api.post("/diet/propose-change", { day: target.day, meal_type: target.meal_type, proposal: t });
      setResult(data);
      if (data.approved) {
        setPlan(data.plan);
        toast.success("Modifica approvata da DietFlix");
        setTimeout(() => setTarget(null), 900);
      } else {
        toast.error("Modifica non approvata da DietFlix");
      }
    } catch (err) {
      showApiError(err);
    } finally { setProposing(false); }
  };

  const applySuggestion = () => {
    if (!result?.suggestion) return;
    doPropose("Applica esattamente questa alternativa che hai proposto: " + JSON.stringify(result.suggestion));
  };

  if (loading) return <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>;

  if (generating) return <Card className="rounded-2xl" data-testid="diet-generating"><CardContent className="p-0"><AiLoader type="diet" title="DietFlix sta preparando la tua dieta" /></CardContent></Card>;

  const openShopping = async () => {
    setShopOpen(true);
    setShopMode("base");
    setRecipeShop(null);
    setShopLoading(true);
    try {
      const { data } = await api.get("/diet/shopping-list");
      setShopItems(data.items || []);
      setShopOrder(data.category_order || []);
      try { setShopChecked(JSON.parse(localStorage.getItem("nc_shop_checked") || "{}")); } catch { setShopChecked({}); }
    } catch (err) {
      showApiError(err);
      setShopOpen(false);
    } finally { setShopLoading(false); }
  };

  const exportPdf = () => {
    const w = window.open("", "_blank");
    if (!w) { toast.error("Consenti i popup per scaricare il PDF"); return; }
    const daysHtml = (plan.days || []).map((d) => (
      `<section class="day"><h2>${d.day} <span class="k">${d.total_kcal} kcal</span></h2>` +
      (d.meals || []).map((m) => (
        `<div class="meal"><div class="mh"><strong>${m.type}</strong> — ${m.name || ""} ` +
        `<span class="k">${m.kcal} kcal · P${m.protein_g} C${m.carbs_g} G${m.fat_g}</span></div>` +
        `<ul>${(m.items || []).map((it) => `<li>${it.food} — ${it.grams}g · ${it.kcal} kcal</li>`).join("")}</ul></div>`
      )).join("") + `</section>`
    )).join("");
    w.document.write(
      `<html><head><title>Dieta settimanale — DietFlix</title><style>
      *{font-family:Georgia,'Times New Roman',serif;color:#1F2923}
      body{margin:32px;max-width:760px}
      h1{color:#2D5A43;font-size:26px;margin-bottom:2px}
      .sub{color:#7C8E81;font-family:Arial,sans-serif;margin-top:0}
      .day{break-inside:avoid;margin:18px 0;padding:14px 18px;border:1px solid #E2DDD3;border-radius:12px}
      .day h2{font-size:18px;color:#2D5A43;margin:0 0 8px}
      .meal{margin:8px 0}.mh{font-family:Arial,sans-serif;font-size:13px}
      .k{color:#D97706;font-family:Arial,sans-serif;font-size:12px}
      ul{margin:4px 0 0 18px;font-family:Arial,sans-serif;font-size:13px;color:#526356}
      </style></head><body>
      <h1>DietFlix — Piano alimentare settimanale</h1>
      <p class="sub">Da appendere al frigo · generato automaticamente</p>
      ${daysHtml}</body></html>`
    );
    w.document.close(); w.focus();
    setTimeout(() => w.print(), 400);
  };

  const groupedShop = () => {
    const order = shopOrder.length ? shopOrder : [...new Set(shopItems.map((i) => i.category))];
    return order
      .map((cat) => ({ cat, items: shopItems.filter((i) => i.category === cat) }))
      .filter((g) => g.items.length);
  };

  const exportShopping = () => {
    const w = window.open("", "_blank");
    if (!w) { toast.error("Consenti i popup per stampare la lista"); return; }
    const groups = groupedShop().map((g) => (
      `<section class="rep"><h2>${g.cat}</h2><ul>` +
      g.items.map((it) => `<li><span>${it.food}</span><span class="q">${it.grams} g · ${it.packages} conf.</span></li>`).join("") +
      `</ul></section>`
    )).join("");
    w.document.write(
      `<html><head><title>Lista della spesa — DietFlix</title><style>
      *{font-family:Arial,Helvetica,sans-serif;color:#1F2923}
      body{margin:32px;max-width:640px}
      h1{color:#2D5A43;font-size:24px;margin-bottom:2px}
      .sub{color:#7C8E81;margin-top:0}
      .rep{break-inside:avoid;margin:14px 0}
      .rep h2{font-size:15px;color:#D97706;border-bottom:1px solid #E2DDD3;padding-bottom:4px;margin:0 0 6px}
      ul{list-style:none;margin:0;padding:0}
      li{display:flex;justify-content:space-between;padding:5px 2px;border-bottom:1px dashed #Eee;font-size:14px}
      .q{color:#526356;font-family:monospace}
      </style></head><body>
      <h1>Lista della spesa</h1><p class="sub">DietFlix · quantità totali settimanali</p>
      ${groups}</body></html>`
    );
    w.document.close(); w.focus();
    setTimeout(() => w.print(), 400);
  };

  if (!plan?.days?.length) {
    return (
      <Card className="rounded-2xl">
        <CardContent className="py-16 text-center space-y-4">
          <div className="h-14 w-14 rounded-2xl bg-secondary grid place-items-center mx-auto"><CalendarDays className="h-7 w-7 text-primary" /></div>
          <h3 className="font-heading text-2xl font-semibold">{premium ? "Genera il tuo piano settimanale" : "Prova la tua giornata su misura"}</h3>
          <p className="text-muted-foreground max-w-md mx-auto">{premium
            ? "DietFlix analizza abitudini, analisi del sangue e attività fisica per creare 7 giorni di pasti bilanciati sul tuo obiettivo."
            : "DietFlix analizza il tuo profilo e crea il tuo lunedì completo: 5 pasti bilanciati sul tuo obiettivo. Con Premium sblocchi tutti i 7 giorni."}</p>
          <Button onClick={generate} disabled={generating} className="btn-brand border-0 rounded-full" data-testid="generate-diet-button">
            <Sparkles className="h-4 w-4 mr-2" /> {generating ? "Generazione in corso…" : premium ? "Genera dieta settimanale" : "Genera il mio lunedì"}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5" data-testid="diet-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-semibold">{premium ? "Piano settimanale" : "La tua giornata tipo"}</h2>
          <p className="text-muted-foreground text-sm">Proponi una modifica a un pasto: DietFlix la valuta e la approva.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 mr-1" data-testid="dietary-style-control">
            <span className="text-sm text-muted-foreground hidden sm:inline">Stile:</span>
            <Select value={dietStyle} onValueChange={changeDietStyle} disabled={savingStyle || generating}>
              <SelectTrigger className="h-9 w-[150px]" data-testid="dietary-style-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="onnivoro" data-testid="dietary-style-onnivoro">Onnivoro</SelectItem>
                <SelectItem value="vegetariano" data-testid="dietary-style-vegetariano">Vegetariano</SelectItem>
                <SelectItem value="vegano" data-testid="dietary-style-vegano">Vegano</SelectItem>
                <SelectItem value="pescetariano" data-testid="dietary-style-pescetariano">Pescetariano</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 mr-1" data-testid="fav-frequency-control">
            <span className="text-sm text-muted-foreground hidden sm:inline">Preferiti:</span>
            <Select value={String(favMax)} onValueChange={changeFavMax} disabled={savingStyle || generating}>
              <SelectTrigger className="h-9 w-[120px]" data-testid="fav-frequency-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                  <SelectItem key={n} value={String(n)} data-testid={`fav-frequency-${n}`}>{n}x / sett.</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {!regenRequired && (
            <>
              <Button variant="outline" onClick={guard(openSaved)} data-testid="saved-recipes-button">
                {premium ? <Bookmark className="h-4 w-4 mr-2" /> : <Lock className="h-4 w-4 mr-2" />} Ricette salvate
              </Button>
              <Button variant="outline" onClick={guard(openShopping)} data-testid="shopping-list-button">
                {premium ? <ShoppingCart className="h-4 w-4 mr-2" /> : <Lock className="h-4 w-4 mr-2" />} Lista della spesa
              </Button>
              <Button variant="outline" onClick={guard(exportPdf)} data-testid="export-pdf-button">
                {premium ? <Printer className="h-4 w-4 mr-2" /> : <Lock className="h-4 w-4 mr-2" />} Scarica PDF
              </Button>
            </>
          )}
          {!premium && freeRecipe && (
            <Button variant="outline" onClick={() => openRecipe({ name: freeRecipe.meal_name, type: freeRecipe.meal_type })} data-testid="free-recipe-open-button">
              <Gift className="h-4 w-4 mr-2 text-accent" /> Ricetta omaggio
            </Button>
          )}
          <Button variant="outline" onClick={generate} disabled={generating} data-testid="regenerate-diet-button">
            <RefreshCw className="h-4 w-4 mr-2" /> {premium ? "Rigenera settimana" : "Rigenera giornata"}
          </Button>
        </div>
      </div>

      {premium && partial && !regenRequired && (
        <div className="rounded-2xl bg-brand text-white p-5 flex flex-col sm:flex-row sm:items-center gap-3 shadow-lg pop-in" data-testid="diet-full-week-banner">
          <div className="flex-1">
            <div className="font-heading text-lg font-semibold">Benvenuto in Premium! Sblocca tutta la settimana</div>
            <p className="text-sm text-white/85">Genera ora i 7 giorni completi della tua dieta su misura.</p>
          </div>
          <Button onClick={generate} disabled={generating} className="bg-white text-[#7A2FF2] hover:bg-white/90 rounded-full font-semibold" data-testid="diet-full-week-button">
            <Sparkles className="h-4 w-4 mr-2" /> Genera la settimana completa
          </Button>
        </div>
      )}

      {regenRequired && (
        <div className="rounded-2xl border-2 border-amber-400 bg-amber-50 text-amber-900 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="regen-required-banner">
          <div className="flex-1">
            <div className="font-semibold flex items-center gap-2"><RefreshCw className="h-4 w-4" /> Dieta non aggiornata</div>
            <p className="text-sm mt-0.5">Hai cambiato lo stile alimentare in <strong>{DIETARY_LABELS[dietStyle] || dietStyle}</strong>. Rigenera la dieta per allinearla al nuovo stile.</p>
          </div>
          <Button onClick={generate} disabled={generating} className="bg-amber-500 hover:bg-amber-600 text-white shrink-0" data-testid="regen-now-button">
            <Sparkles className="h-4 w-4 mr-2" /> Rigenera ora
          </Button>
        </div>
      )}

      {regenRequired && (
        <div className="rounded-2xl border-2 border-dashed border-muted-foreground/30 bg-muted/30 p-10 text-center space-y-3" data-testid="diet-locked">
          <div className="h-12 w-12 rounded-2xl bg-muted grid place-items-center mx-auto"><RefreshCw className="h-6 w-6 text-muted-foreground" /></div>
          <h3 className="font-heading text-xl font-semibold">Dieta bloccata</h3>
          <p className="text-muted-foreground max-w-md mx-auto text-sm">La dieta precedente non è più valida dopo la modifica. Rigenerala per vederla aggiornata e sbloccare ricette, lista della spesa e PDF.</p>
          <Button onClick={generate} disabled={generating} data-testid="diet-locked-regen-button">
            <Sparkles className="h-4 w-4 mr-2" /> Rigenera la dieta
          </Button>
        </div>
      )}

      {!regenRequired && (
      <Accordion type="single" collapsible defaultValue={plan.days[0]?.day} className="space-y-3">
        {plan.days.map((d) => (
          <AccordionItem key={d.day} value={d.day} className="border rounded-2xl bg-card px-4" data-testid={`diet-day-${d.day}`}>
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center justify-between w-full pr-3">
                <span className="font-heading text-lg font-semibold">{d.day}</span>
                <Badge variant="secondary" className="font-mono-data">{d.total_kcal} kcal</Badge>
              </div>
            </AccordionTrigger>
            <AccordionContent className="space-y-3 pb-4">
              {(d.meals || []).map((m) => (
                <div key={m.type} className="border rounded-xl p-4 bg-background" data-testid={`meal-${d.day}-${m.type}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <Badge className={`${MEAL_COLORS[m.type] || "bg-secondary"} border mb-1.5`}>{m.type}</Badge>
                      <div className="font-medium">{m.name}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="sm" variant="ghost" className={`text-primary hover:text-primary ${!premium && !freeRecipe ? "bg-accent/10 text-accent hover:text-accent" : ""}`}
                        onClick={() => recipeClick(m)} data-testid={`recipe-button-${d.day}-${m.type}`}>
                        {premium ? <><ChefHat className="h-4 w-4 mr-1" /> Ricetta</>
                          : isGift(m) ? <><ChefHat className="h-4 w-4 mr-1" /> La tua ricetta</>
                          : !freeRecipe ? <><Gift className="h-4 w-4 mr-1" /> Ricetta omaggio</>
                          : <><Lock className="h-3.5 w-3.5 mr-1" /> Ricetta</>}
                      </Button>
                      <Button size="sm" variant="ghost" className="text-accent hover:text-accent"
                        onClick={guard(() => openPropose(d.day, m.type))}
                        data-testid={`propose-button-${d.day}-${m.type}`}>
                        {premium ? <Wand2 className="h-4 w-4 mr-1" /> : <Lock className="h-3.5 w-3.5 mr-1" />} Proponi a DietFlix
                      </Button>
                    </div>
                  </div>
                  <ul className="text-sm text-muted-foreground space-y-0.5 mb-2">
                    {(m.items || []).map((it, i) => (
                      <li key={i} className="flex justify-between">
                        <span>{it.food}</span>
                        <span className="font-mono-data">{it.grams}g · {it.kcal} kcal</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="rounded-md bg-amber-100 text-amber-900 px-2 py-0.5 font-mono-data">{m.kcal} kcal</span>
                    <span className="rounded-md bg-emerald-100 text-emerald-900 px-2 py-0.5 font-mono-data">P {m.protein_g}g</span>
                    <span className="rounded-md bg-orange-100 text-orange-900 px-2 py-0.5 font-mono-data">C {m.carbs_g}g</span>
                    <span className="rounded-md bg-yellow-100 text-yellow-900 px-2 py-0.5 font-mono-data">G {m.fat_g}g</span>
                  </div>
                  {m.note && <p className="text-xs text-muted-foreground mt-2 italic">{m.note}</p>}
                  {supps.filter((s) => s.meal === m.type).map((s, si) => (
                    <div key={si} className="mt-2 flex items-center gap-2 rounded-lg border border-primary/25 bg-primary/5 px-2.5 py-1.5 text-xs"
                      data-testid={`meal-supp-${d.day}-${m.type}-${si}`}>
                      <Pill className="h-3.5 w-3.5 text-primary shrink-0" />
                      <span className="font-medium text-primary">Integratore:</span>
                      <span className="text-muted-foreground">{s.name} · {s.dosage}</span>
                    </div>
                  ))}
                </div>
              ))}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      )}

      {!premium && !regenRequired && lockedDays.length > 0 && (
        <div className="space-y-4">
          <PremiumLock testid="premium-lock-overlay-diet-days" title={`Sblocca gli altri ${lockedDays.length} giorni della tua dieta`}
            description="Il tuo piano completo ti aspetta: pasti sempre diversi, ricette passo-passo e lista della spesa automatica."
            perks={["7 giorni su 7 su misura", "Ricette e modalità cucina", "Lista della spesa e PDF", "Modifiche ai pasti approvate da DietFlix"]}
            cta="Sblocca la settimana">
            <div className="space-y-3 p-1">
              {lockedDays.map((day) => (
                <div key={day} className="border rounded-2xl bg-card px-4 py-4 flex items-center justify-between" data-testid={`locked-day-${day}`}>
                  <span className="font-heading text-lg font-semibold">{day}</span>
                  <Badge variant="secondary" className="font-mono-data">{plan.days[0]?.total_kcal || 1800} kcal</Badge>
                </div>
              ))}
            </div>
          </PremiumLock>
        </div>
      )}

      <Dialog open={!!recipe} onOpenChange={(o) => { if (!o) { window.speechSynthesis && window.speechSynthesis.cancel(); setSpeaking(false); setRecipe(null); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="recipe-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 pr-8"><ChefHat className="h-5 w-5 text-primary shrink-0" /> {recipe?.data?.title || recipe?.mealName || "Ricetta"}</DialogTitle>
            <DialogDescription>Ricetta su misura per il tuo piatto, per una porzione.</DialogDescription>
            {!premium && (
              <div className="mt-2 rounded-xl bg-accent/10 border border-accent/30 p-3 text-xs flex items-center gap-2" data-testid="free-recipe-note">
                <Gift className="h-4 w-4 text-accent shrink-0" />
                <span className="flex-1">Questa è la tua <b>ricetta omaggio</b>. Con Premium hai le ricette di tutti i pasti, per più porzioni.</span>
                <Button size="sm" className="btn-premium border-0 rounded-full h-7 text-xs" onClick={openUpgrade} data-testid="free-recipe-upgrade">Premium</Button>
              </div>
            )}
          </DialogHeader>
          {recipe?.loading ? (
            <div className="flex flex-col items-center justify-center py-10 gap-3" data-testid="recipe-loading">
              <ChefHat className="h-10 w-10 text-primary animate-bounce" />
              <p className="text-sm text-muted-foreground">Sto scrivendo la ricetta…</p>
            </div>
          ) : recipe?.data ? (
            <div className="space-y-4" data-testid="recipe-content">
              <div className="flex flex-wrap gap-2 text-xs">
                {recipe.data.prep_min != null && <span className="rounded-md bg-secondary px-2 py-1 inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Prep {recipe.data.prep_min} min</span>}
                {recipe.data.cook_min != null && <span className="rounded-md bg-secondary px-2 py-1 inline-flex items-center gap-1"><Utensils className="h-3.5 w-3.5" /> Cottura {recipe.data.cook_min} min</span>}
              </div>
              {recipe.data.nutrition && (
                <div className="grid grid-cols-4 gap-2" data-testid="recipe-nutrition">
                  {[["kcal", "kcal", recipe.data.nutrition.kcal], ["Prot", "g", recipe.data.nutrition.protein_g], ["Carb", "g", recipe.data.nutrition.carbs_g], ["Grassi", "g", recipe.data.nutrition.fat_g]].map(([lab, unit, val]) => (
                    <div key={lab} className="rounded-xl bg-primary/5 border border-primary/15 py-2 text-center">
                      <div className="font-heading font-bold text-primary">{val ?? "—"}<span className="text-[10px] font-normal ml-0.5"> {unit}</span></div>
                      <div className="text-[11px] text-muted-foreground">{lab}</div>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between rounded-xl border p-2.5">
                <span className="text-sm font-medium">Porzioni</span>
                <div className="flex items-center gap-3">
                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => changeServings((recipe.servings || 1) - 1)} disabled={!premium || recipe.loading || (recipe.servings || 1) <= 1} data-testid="recipe-servings-dec">−</Button>
                  <span className="w-6 text-center font-heading font-bold" data-testid="recipe-servings-value">{recipe.servings || 1}</span>
                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => premium ? changeServings((recipe.servings || 1) + 1) : openUpgrade()} disabled={recipe.loading || (recipe.servings || 1) >= 12} data-testid="recipe-servings-inc">+</Button>
                </div>
              </div>
              {(recipe.data.ingredients || []).length > 0 && (
                <div>
                  <div className="font-medium mb-1.5">Ingredienti</div>
                  <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
                    {recipe.data.ingredients.map((x, i) => <li key={i}>{x}</li>)}
                  </ul>
                </div>
              )}
              {(recipe.data.steps || []).length > 0 && (
                <div>
                  <div className="font-medium mb-1.5">Preparazione</div>
                  <ol className="text-sm text-muted-foreground list-decimal pl-5 space-y-1.5">
                    {recipe.data.steps.map((x, i) => <li key={i}>{x}</li>)}
                  </ol>
                </div>
              )}
              {(recipe.data.tips || []).length > 0 && (
                <div className="rounded-xl border border-accent/30 bg-accent/5 p-3">
                  <div className="font-medium text-accent mb-1 flex items-center gap-1.5"><Sparkles className="h-4 w-4" /> Consigli</div>
                  <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
                    {recipe.data.tips.map((x, i) => <li key={i}>{x}</li>)}
                  </ul>
                </div>
              )}
            </div>
          ) : null}
          {recipe?.data && (
            <DialogFooter className="flex-row flex-wrap gap-2 sm:justify-start">
              <Button variant="default" size="sm" onClick={guard(saveRecipe)} disabled={savedIds.includes(recipeTitle())} data-testid="recipe-save-button">
                <Bookmark className="h-4 w-4 mr-1.5" /> {savedIds.includes(recipeTitle()) ? "Salvata" : "Salva"}
              </Button>
              <Button variant="outline" size="sm" onClick={speakRecipe} data-testid="recipe-speak-button">
                {speaking ? <><Square className="h-4 w-4 mr-1.5" /> Ferma</> : <><Volume2 className="h-4 w-4 mr-1.5" /> Leggi</>}
              </Button>
              <Button variant="outline" size="sm" onClick={printRecipe} data-testid="recipe-print-button">
                <Printer className="h-4 w-4 mr-1.5" /> Stampa
              </Button>
              <Button variant="outline" size="sm" onClick={() => setCooking(recipe.data)} disabled={!(recipe.data.steps || []).length} data-testid="recipe-cooking-button">
                <Flame className="h-4 w-4 mr-1.5" /> Modalità cucina
              </Button>
              <Button variant="outline" size="sm" onClick={shareRecipe} data-testid="recipe-share-button">
                <Share2 className="h-4 w-4 mr-1.5" /> Condividi
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {cooking && <CookingMode recipe={cooking} onClose={() => setCooking(null)} />}

      <Dialog open={savedOpen} onOpenChange={setSavedOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="saved-recipes-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Bookmark className="h-5 w-5 text-primary" /> Ricette salvate</DialogTitle>
            <DialogDescription>La tua raccolta personale di ricette.</DialogDescription>
          </DialogHeader>
          {savedLoading ? (
            <div className="space-y-2 py-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12 rounded-lg" />)}</div>
          ) : savedList.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center" data-testid="saved-recipes-empty">Nessuna ricetta salvata. Apri una ricetta e tocca "Salva".</p>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input value={savedQuery} onChange={(e) => setSavedQuery(e.target.value)} placeholder="Cerca ricetta…" className="pl-8" data-testid="saved-recipes-search" />
                </div>
                <Button variant="outline" size="sm" onClick={() => setSavedSort((s) => s === "recent" ? "az" : "recent")} data-testid="saved-recipes-sort">
                  <ArrowDownUp className="h-4 w-4 mr-1.5" /> {savedSort === "recent" ? "Recenti" : "A-Z"}
                </Button>
              </div>
              <div className="space-y-2" data-testid="saved-recipes-list">
                {savedList
                  .filter((r) => r.name.toLowerCase().includes(savedQuery.trim().toLowerCase()))
                  .slice()
                  .sort((a, b) => savedSort === "az" ? a.name.localeCompare(b.name) : (b.created_at || "").localeCompare(a.created_at || ""))
                  .map((r) => (
                  <div key={r.recipe_id} className="flex items-center justify-between gap-2 rounded-lg border p-3" data-testid={`saved-recipe-${r.recipe_id}`}>
                    <button className="text-left text-sm font-medium hover:text-primary flex items-center gap-2 min-w-0"
                      onClick={() => { setSavedOpen(false); setRecipe({ loading: false, data: r.data, mealName: r.name, servings: r.data?.servings || 1 }); }}
                      data-testid={`saved-recipe-open-${r.recipe_id}`}>
                      <ChefHat className="h-4 w-4 text-primary shrink-0" /> <span className="truncate">{r.name}</span>
                    </button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive shrink-0" onClick={() => deleteSaved(r.recipe_id)} data-testid={`saved-recipe-delete-${r.recipe_id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                {savedList.filter((r) => r.name.toLowerCase().includes(savedQuery.trim().toLowerCase())).length === 0 && (
                  <p className="text-sm text-muted-foreground py-4 text-center">Nessun risultato per "{savedQuery}".</p>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent data-testid="propose-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Utensils className="h-5 w-5" /> Proponi una modifica</DialogTitle>
            <DialogDescription>{target?.meal_type} di {target?.day}. DietFlix dovrà approvare la tua proposta.</DialogDescription>
          </DialogHeader>
          <Textarea placeholder="es. Vorrei sostituire il pollo con del salmone, oppure ridurre i carboidrati a cena…"
            value={proposal} onChange={(e) => setProposal(e.target.value)} data-testid="propose-input" />

          {result && (
            <div className={`rounded-xl border p-3 text-sm space-y-3 ${result.approved ? "bg-primary/5 border-primary/30" : "bg-destructive/5 border-destructive/30"}`}
              data-testid="propose-result">
              <div className="flex gap-2">
                {result.approved
                  ? <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  : <XCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />}
                <div>
                  <div className="font-medium">{result.approved ? "Modifica approvata" : "Modifica non approvata"}</div>
                  <p className="text-muted-foreground">{result.reason}</p>
                </div>
              </div>
              {result.estimate && result.target && (result.estimate.kcal != null || result.estimate.fat_g != null) && (
                <div className="space-y-2 rounded-lg bg-background/60 border p-2.5" data-testid="propose-compare">
                  <div className="text-xs font-medium text-muted-foreground">Proposto vs consigliato</div>
                  <CompareBar label="Calorie" unit=" kcal" proposed={result.estimate.kcal} target={result.target.kcal} testid="compare-kcal" />
                  <CompareBar label="Grassi" unit="g" proposed={result.estimate.fat_g} target={result.target.fat_g} testid="compare-fat" />
                </div>
              )}
              {!result.approved && result.suggestion && (
                <div className="space-y-2">
                  <div className="text-xs font-medium text-muted-foreground">Alternativa suggerita da DietFlix</div>
                  <MealCard meal={result.suggestion} testid="propose-suggestion-meal" />
                  <Button size="sm" onClick={applySuggestion} disabled={proposing} data-testid="apply-suggestion-button">
                    <CheckCircle2 className="h-4 w-4 mr-2" /> Applica questa alternativa
                  </Button>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => doPropose()} disabled={proposing} data-testid="confirm-propose-button">
              <Wand2 className="h-4 w-4 mr-2" /> {proposing ? "DietFlix sta valutando…" : "Proponi a DietFlix"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={shopOpen} onOpenChange={setShopOpen}>
        <DialogContent className="max-w-lg" data-testid="shopping-list-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><ShoppingCart className="h-5 w-5" /> Lista della spesa</DialogTitle>
            <DialogDescription>Quantità totali della settimana, raggruppate per reparto.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-1 rounded-lg bg-muted p-1">
            <button onClick={() => setShopMode("base")} data-testid="shop-mode-base"
              className={`text-sm rounded-md py-1.5 font-medium transition-colors ${shopMode === "base" ? "bg-background shadow-sm" : "text-muted-foreground"}`}>
              Quantità dieta
            </button>
            <button onClick={loadRecipeShop} data-testid="shop-mode-recipes"
              className={`text-sm rounded-md py-1.5 font-medium transition-colors inline-flex items-center justify-center gap-1.5 ${shopMode === "recipes" ? "bg-background shadow-sm" : "text-muted-foreground"}`}>
              <ChefHat className="h-3.5 w-3.5" /> Da ricette DietFlix
            </button>
          </div>
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">Spunta ciò che hai già preso.</p>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={clearChecked} disabled={shopLoading} data-testid="clear-checked-button">
                Azzera spunte
              </Button>
              {shopMode === "base" && (
                <Button size="sm" variant="outline" onClick={exportShopping} disabled={shopLoading || !shopItems.length} data-testid="export-shopping-button">
                  <Printer className="h-4 w-4 mr-2" /> Stampa / PDF
                </Button>
              )}
            </div>
          </div>
          {shopMode === "recipes" ? (
            recipeShopLoading ? (
              <div className="flex flex-col items-center justify-center py-10 gap-3" data-testid="recipe-shop-loading">
                <ChefHat className="h-9 w-9 text-primary animate-bounce" />
                <p className="text-sm text-muted-foreground">Compongo la lista dalle ricette…</p>
              </div>
            ) : (
              <div className="max-h-[55vh] overflow-y-auto space-y-4" data-testid="recipe-shop-list">
                {(recipeShop || []).map((g, gi) => (
                  <div key={gi} data-testid={`recipe-shop-cat-${gi}`}>
                    <div className="text-sm font-semibold text-accent border-b pb-1 mb-1">{g.category}</div>
                    <div className="divide-y">
                      {(g.items || []).map((it, i) => {
                        const key = `r:${it.name}`;
                        const done = !!shopChecked[key];
                        return (
                          <label key={i} className="flex items-center gap-3 py-2 cursor-pointer" data-testid={`recipe-shop-item-${gi}-${i}`}>
                            <Checkbox checked={done} onCheckedChange={() => toggleChecked(key)} />
                            <span className={`font-medium flex-1 ${done ? "line-through text-muted-foreground" : ""}`}>{it.name}</span>
                            <span className="font-mono-data text-sm text-muted-foreground shrink-0">{it.quantity}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {(!recipeShop || recipeShop.length === 0) && <p className="text-muted-foreground py-6 text-center">Nessun elemento.</p>}
              </div>
            )
          ) : shopLoading ? (
            <div className="space-y-2">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-8 rounded-lg" />)}</div>
          ) : (
            <div className="max-h-[55vh] overflow-y-auto space-y-4">
              {groupedShop().map((g) => (
                <div key={g.cat} data-testid={`shopping-cat-${g.cat}`}>
                  <div className="text-sm font-semibold text-accent border-b pb-1 mb-1">{g.cat}</div>
                  <div className="divide-y">
                    {g.items.map((it, i) => {
                      const done = !!shopChecked[it.food];
                      return (
                        <label key={i} className="flex items-center gap-3 py-2 cursor-pointer" data-testid={`shopping-item-${it.food}`}>
                          <Checkbox checked={done} onCheckedChange={() => toggleChecked(it.food)} data-testid={`shopping-check-${it.food}`} />
                          <span className={`font-medium flex-1 ${done ? "line-through text-muted-foreground" : ""}`}>{it.food}</span>
                          <span className="font-mono-data text-sm text-muted-foreground shrink-0">{it.grams} g · {it.packages} conf.</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
              {shopItems.length === 0 && <p className="text-muted-foreground py-6 text-center">Nessun elemento.</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
